<template>
  <CModal
    :show="$store.state.support.show"
    :centered="true"
    :close-on-backdrop="false"
    size="xl"
    title="Contact Support"
    @update:show="close"
  >
    <RSpinnerOverlay :loading="$store.state.support.loading" :opacity="0.7">
      <CForm>
        <CRow class="mb-4">
          <CCol md="12">
            <RDropdownMultiSelect
              v-model="$v.selectedIssues.$model"
              :options="supportOptions"
              :is-valid="validateField('selectedIssues')"
              add-label-classes="required"
            >
              {{ $v.selectedIssues.$model.length === 0 ? 'What problem(s) are you experiencing?' : $v.selectedIssues.$model.length + ' issue type(s) selected' }}
            </RDropdownMultiSelect>
            <div
              v-if="$v.selectedIssues.$error && !$v.selectedIssues.multiSelectValidator"
              class="invalid-feedback checkbox"
            >
              At least one issue type must be selected
            </div>
          </CCol>
        </CRow>
        <CRow class="mt-2">
          <CCol>
            <CTextarea
              v-model.trim="$v.description.$model"
              :is-valid="validateField('description')"
              rows="5"
              label="Can you tell us more about the problem you're experiencing?"
              invalid-feedback="This field must not exceed 2048 characters"
            />
          </CCol>
        </CRow>
        <CRow class="mt-2">
          <CCol>
            <CInput
              v-model.trim="$v.name.$model"
              :is-valid="validateField('name')"
              add-label-classes="required"
              label="Your Name"
            >
              <template #invalid-feedback>
                <div v-if="!$v.name.required" class="invalid-feedback">
                  This field is required
                </div>
                <div v-if="!$v.name.maxLength" class="invalid-feedback">
                  This field must not exceed 128 characters
                </div>
              </template>
            </CInput>
          </CCol>
        </CRow>
        <CRow class="mt-2">
          <CCol md="6">
            <CInput
              v-model.trim="$v.email.$model"
              :is-valid="validateField('email')"
              add-label-classes="required"
              label="Email Address"
              description="We will reply to this email"
            >
              <template #invalid-feedback>
                <div v-if="!$v.email.required" class="invalid-feedback">
                  This field is required
                </div>
                <div v-if="!$v.email.email" class="invalid-feedback">
                  This field must be an email
                </div>
              </template>
            </CInput>
          </CCol>
          <CCol md="6">
            <CInput
              v-model.trim="$v.phone.$model"
              :is-valid="validateField('phone')"
              label="Direct Phone Number"
              invalid-feedback="This field must not exceed 32 characters"
              description="We may call you at this number"
            />
          </CCol>
        </CRow>
      </CForm>
    </RSpinnerOverlay>
    <template #footer>
      <CButton @click="close">
        Cancel
      </CButton>
      <CButton class="btn-info" :disabled="$store.state.support.loading" @click="submit">
        Send Report
      </CButton>
    </template>
  </CModal>
</template>

<script>
/**
 * ContactSupportModal
 *
 * State is controlled by the `support` module in
 * vuex. To show the modal, do
 *
 *   this.$store.dispatch('support/open')
 *
 */
import { validationMixin } from 'vuelidate'
import { email, maxLength, required } from 'vuelidate/lib/validators'

import { RDropdownMultiSelect } from '@/components/inputs'
import { RSpinnerOverlay } from '@/components/spinners'

/**
 * Validation function to ensure at least one issue is selected
 */
const multiSelectValidator = (val) => {
  return val && val.length > 0
}

export default {
  name: 'ContactSupportModal',
  components: {
    RSpinnerOverlay,
    RDropdownMultiSelect
  },
  mixins: [validationMixin],
  validations: {
    selectedIssues: { multiSelectValidator },
    description: { maxLength: maxLength(2048) },
    name: { required, maxLength: maxLength(128) },
    email: { required, email },
    phone: { maxLength: maxLength(32) }
  },
  computed: {
    supportOptions () {
      return Object.entries(this.$store.state.support.labels).map(([value, label]) => ({
        value,
        label,
        text: label // RDropdownMultiSelect expects a text property
      }))
    },
    selectedIssues: {
      get () {
        // Return the keys of issues that are true in the store
        return Object.keys(this.$store.state.support.labels).filter(key =>
          this.$store.state.support[key]
        )
      },
      set (value) {
        // First reset all issues to false
        Object.keys(this.$store.state.support.labels).forEach(key => {
          this.$store.commit('support/set', [key, false])
        })
        // Then set selected issues to true
        value.forEach(key => {
          this.$store.commit('support/set', [key, true])
        })
      }
    },
    description: {
      get () {
        return this.$store.state.support.description
      },
      set (value) {
        this.$store.commit('support/set', ['description', value])
      }
    },
    name: {
      get () {
        return this.$store.state.support.user_name
      },
      set (value) {
        this.$store.commit('support/set', ['user_name', value])
      }
    },
    email: {
      get () {
        return this.$store.state.support.user_email
      },
      set (value) {
        this.$store.commit('support/set', ['user_email', value])
      }
    },
    phone: {
      get () {
        return this.$store.state.support.user_phone
      },
      set (value) {
        this.$store.commit('support/set', ['user_phone', value])
      }
    }
  },
  methods: {
    /**
   * Validate a form field.
   *
   * @param {String} fieldName
   * @returns {Boolean}
   */
    validateField (fieldName) {
      const field = this.$v[fieldName]
      if (!field.$dirty) {
        return null
      }
      return !field.$invalid
    },
    /**
     * Resets validation and dispatches close action.
     */
    close () {
      this.$v.$reset()
      this.$store.dispatch('support/close')
    },
    /**
     * Validates and submits form
     */
    async submit () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }
      try {
        this.$store.commit('support/set', ['loading', true])
        await this.$store.dispatch('support/submit')
        this.close()
        this.$store.commit('notification/show', {
          message: 'Thank you! Our support team will be in touch soon.',
          type: 'success'
        })
      } catch (err) {
        this.$store.commit('support/set', ['loading', false])
        this.close()
        this.$store.dispatch('error/show', err)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .required::after {
  content: '*';
}
.invalid-feedback.checkbox {
  display: block;
}
::v-deep .dropdown-multiselect {
  button {
    width: 100%;
    text-align: left;
    display: flex;
    svg {
      margin-left: auto;
    }
  }
  .dropdown-menu {
    width: 100%;
  }
}
</style>
